// ============================================================================
// MarketWalletOperations — история операций кошелька в виде списка карточек.
//
// Строки собирает lib/marketWallet: «Списано за заявку №…», «Возврат за
// заявку №…», пополнение, резервы/переводы. Здесь — только представление:
// дата, понятный текст, знак и состояние. Без таблиц.
// ============================================================================

import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  Lock,
} from 'lucide-react';
import { formatValue } from '@/lib/format';
import {
  WALLET_STATE_LABELS,
  type MarketWalletOperation,
} from '@/lib/marketWallet';

function money(value: number): string {
  return formatValue(value, 'number:0;group', 'numeric');
}

function amountLabel(operation: MarketWalletOperation): string {
  const value = money(operation.amount);
  if (operation.tone === 'in') return `+${value}`;
  if (operation.tone === 'out') return `−${value}`;
  return value;
}

function toneIcon(tone: MarketWalletOperation['tone']) {
  if (tone === 'in') return <ArrowDownLeft className="h-4 w-4" />;
  if (tone === 'out') return <ArrowUpRight className="h-4 w-4" />;
  if (tone === 'reserve') return <Lock className="h-4 w-4" />;
  return <ArrowLeftRight className="h-4 w-4" />;
}

function toneClass(tone: MarketWalletOperation['tone']): string {
  if (tone === 'in') return 'bg-emerald-500/10 text-emerald-700';
  if (tone === 'out') return 'bg-primary/10 text-primary';
  if (tone === 'reserve') return 'bg-amber-500/10 text-amber-700';
  return 'bg-muted text-muted-foreground';
}

function amountClass(operation: MarketWalletOperation): string {
  if (operation.inactive) return 'text-muted-foreground line-through';
  if (operation.tone === 'in') return 'text-emerald-700';
  if (operation.tone === 'out') return 'text-foreground';
  return 'text-muted-foreground';
}

function stateBadge(operation: MarketWalletOperation) {
  const label = WALLET_STATE_LABELS[operation.state];
  if (!label || operation.state === 'done') return null;
  const cls = operation.state === 'failed'
    ? 'border-destructive/30 bg-destructive/5 text-destructive'
    : 'border-amber-500/30 bg-amber-500/5 text-amber-700';
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${cls}`}>
      {label}
    </span>
  );
}

interface MarketWalletOperationsProps {
  operations: MarketWalletOperation[];
  currency?: string;
}

export function MarketWalletOperations({ operations, currency }: MarketWalletOperationsProps) {
  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">История операций</h2>
        <span className="text-[11px] text-muted-foreground">
          {operations.length > 0 ? `Показаны последние ${operations.length}` : ''}
        </span>
      </div>

      {operations.length === 0 ? (
        <div className="rounded-2xl border border-dashed bg-card/50 px-4 py-6 text-center text-sm text-muted-foreground">
          Операций пока нет. Пополнения и списания за заявки появятся здесь.
        </div>
      ) : (
        <ul className="space-y-2">
          {operations.map((operation) => (
            <li
              key={operation.id}
              className="rounded-2xl border bg-card p-3 shadow-sm"
            >
              <div className="flex items-start gap-3">
                <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${toneClass(operation.tone)}`}>
                  {toneIcon(operation.tone)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                    <div className="min-w-0 flex-1 break-words text-sm font-medium leading-5">
                      {operation.title}
                    </div>
                    <div className={`shrink-0 text-sm font-semibold tabular-nums ${amountClass(operation)}`}>
                      {amountLabel(operation)}
                      {currency && (
                        <span className="ml-1 text-[10px] font-normal text-muted-foreground">
                          {currency}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-0.5 break-words text-[11px] leading-4 text-muted-foreground">
                    {operation.subtitle}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <span className="text-[11px] text-muted-foreground">
                      {operation.createdAt
                        ? formatValue(operation.createdAt, 'datetime:dd.MM.yyyy HH:mm', 'timestamp')
                        : 'Дата неизвестна'}
                    </span>
                    {stateBadge(operation)}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
